import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Search, Server as ServerIcon } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button, DataTable, EmptyState, formatBytes, formatRelative, Input, PageHeader, Pagination, Panel, Select } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { useListParams } from "../../lib/list-params";
import { useSession } from "../../session";
import { ServerStatusBadge, UsageBar } from "./shared";

const PAGE_SIZE = 50;
const STATUSES = ["ACTIVE", "READY", "WARNING", "OFFLINE", "PROVISIONING", "CONNECTING", "TESTING", "NEW", "MAINTENANCE", "DRAINING", "FAILED", "RETIRED"];

export function ServersPage() {
  return (
    <RequirePermission anyOf={["INFRASTRUCTURE_VIEW"]}>
      <Servers />
    </RequirePermission>
  );
}

function Servers() {
  const { can } = useSession();
  const navigate = useNavigate();
  const { values, page, set } = useListParams(["search", "regionId", "status"] as const);
  const [search, setSearch] = useState(values.search);
  const regions = useQuery({ queryKey: ["regions"], queryFn: api.regions.list });
  const q = useQuery({
    queryKey: ["servers", values, page],
    queryFn: () => api.servers.list({ ...values, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
  const filtered = Boolean(values.search || values.regionId || values.status);

  return (
    <>
      <PageHeader
        title="Servers"
        description="All-in-one hosting nodes. Each runs the HostDock Agent, Docker and node-level services."
        actions={can("SERVER_ADD") && can("SERVER_PROVISION") && (
          <Link to="/infrastructure/servers/new"><Button variant="primary" icon={<Plus size={15} />}>Add server</Button></Link>
        )}
      />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <form className="relative min-w-52 flex-1" onSubmit={(e) => { e.preventDefault(); set({ search: search.trim() }); }}>
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, hostname or IP" className="pl-8" aria-label="Search servers" />
          </form>
          <Select value={values.regionId} onChange={(e) => set({ regionId: e.target.value })} className="w-48" aria-label="Region">
            <option value="">All regions</option>
            {regions.data?.items.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-40" aria-label="Status">
            <option value="">All (not retired)</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>)}
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(s) => s.id}
          onRowClick={(s) => navigate(`/infrastructure/servers/${s.id}`)}
          empty={
            filtered ? (
              <EmptyState icon={Search} title="No servers match" />
            ) : (
              <EmptyState
                icon={ServerIcon}
                title="No servers yet"
                description="Add a server and HostDock will provision it automatically."
                action={can("SERVER_ADD") ? <Link to="/infrastructure/servers/new"><Button size="sm" variant="primary">Add server</Button></Link> : undefined}
              />
            )
          }
          columns={[
            {
              key: "name",
              header: "Server",
              cell: (s) => (
                <div className="min-w-0">
                  <p className="font-mono text-[13px] font-medium">{s.name}</p>
                  <p className="truncate text-[12px] text-muted">{s.region.name} · {s.publicIp}</p>
                </div>
              ),
            },
            { key: "status", header: "Status", cell: (s) => <ServerStatusBadge status={s.displayStatus} /> },
            { key: "cpu", header: "CPU", cell: (s) => (s.latest ? <UsageBar used={s.latest.cpuPercent} total={100} /> : <span className="text-subtle">—</span>), hideOnMobile: true },
            { key: "ram", header: "RAM", cell: (s) => (s.latest ? <UsageBar used={s.latest.memUsed} total={s.latest.memTotal} label={formatBytes(s.latest.memTotal)} /> : <span className="text-subtle">—</span>), hideOnMobile: true },
            { key: "disk", header: "Disk", cell: (s) => (s.latest ? <UsageBar used={s.latest.diskUsed} total={s.latest.diskTotal} label={formatBytes(s.latest.diskTotal)} /> : <span className="text-subtle">—</span>), hideOnMobile: true },
            { key: "containers", header: "Containers", cell: (s) => <span className="tabular-nums">{s.latest?.containersRunning ?? "—"}</span>, hideOnMobile: true },
            { key: "seen", header: "Last seen", cell: (s) => <span className="text-muted">{s.lastSeenAt ? formatRelative(s.lastSeenAt) : "Never"}</span> },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
    </>
  );
}
